<template>
  <transition
    name="slide"
    appear
    @after-leave="afterLeave"
  >
    <div
      v-if="visible"
      class="overlay"
    >
      <aside
        v-click-outside="vcoConfig"
        :class="asideClass"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="labelledby || undefined"
        @mousedown="onAsideMousedown"
      >
        <slot />
      </aside>
    </div>
  </transition>
</template>

<script>
import { getCurrentInstance } from 'vue';
import { clickOutside } from '@/directives/clickOutside';
import { useAsideLeave } from '@/composables/useAsideLeave';

let openAsideCount = 0;
let previousBodyOverflow = '';
let previousBodyPaddingRight = '';
let previousBodyPosition = '';
let previousBodyTop = '';
let previousBodyWidth = '';
let savedScrollY = 0;

export default {
  directives: {
    clickOutside,
  },
  props: {
    asideClass: {
      type: [String, Array, Object],
      default: '',
    },
    labelledby: {
      type: String,
      default: '',
    },
  },
  setup() {
    const instance = getCurrentInstance();
    // Release the scroll lock as soon as the aside starts leaving: the backdrop
    // is still opaque then, so restoring the background scroll position stays
    // out of sight instead of flashing the page at the top.
    const { visible, afterLeave } = useAsideLeave(() => instance.proxy.unlockBodyScroll());
    return { visible, afterLeave };
  },
  data() {
    return {
      onKeydown: null,
      vcoConfig: {
        handler: this.handler,
        middleware: this.middleware,
        events: ['dblclick', 'click'],
        isActive: true,
      },
      asideMousedown: false,
      bodyScrollLocked: false,
    };
  },
  created() {
    this.onKeydown = (evt) => {
      evt = evt || window.event;
      if (evt.keyCode === 27) {
        this.close();
      }
    };
    document.addEventListener('keydown', this.onKeydown);
  },
  mounted() {
    this.lockBodyScroll();
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
    this.unlockBodyScroll();
  },
  methods: {
    lockBodyScroll() {
      if (typeof document === 'undefined' || this.bodyScrollLocked) return;

      if (openAsideCount === 0) {
        const { body, documentElement } = document;
        const scrollbarWidth = window.innerWidth - documentElement.clientWidth;

        savedScrollY = window.scrollY;

        previousBodyOverflow = body.style.overflow;
        previousBodyPaddingRight = body.style.paddingRight;
        previousBodyPosition = body.style.position;
        previousBodyTop = body.style.top;
        previousBodyWidth = body.style.width;

        body.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.top = `-${savedScrollY}px`;
        body.style.width = '100%';
        if (scrollbarWidth > 0) {
          body.style.paddingRight = `${scrollbarWidth}px`;
        }
      }

      openAsideCount += 1;
      this.bodyScrollLocked = true;
    },
    unlockBodyScroll() {
      if (typeof document === 'undefined' || !this.bodyScrollLocked) return;

      openAsideCount = Math.max(openAsideCount - 1, 0);
      this.bodyScrollLocked = false;

      if (openAsideCount === 0) {
        document.body.style.overflow = previousBodyOverflow;
        document.body.style.paddingRight = previousBodyPaddingRight;
        document.body.style.position = previousBodyPosition;
        document.body.style.top = previousBodyTop;
        document.body.style.width = previousBodyWidth;

        // Bootstrap's reboot sets `:root { scroll-behavior: smooth }`, which would
        // make this restore animate: the page visibly scrolls from the top back to
        // where it was. Force it instant for the duration of the call.
        const { documentElement } = document;
        const previousScrollBehavior = documentElement.style.scrollBehavior;
        documentElement.style.scrollBehavior = 'auto';
        window.scrollTo(0, savedScrollY);
        documentElement.style.scrollBehavior = previousScrollBehavior;
      }
    },
    close() {
      this.$emit('close', true);
    },
    onClickOutside() {
    },
    onAsideMousedown() {
      this.asideMousedown = true;
      window.addEventListener('mouseup', () => {
        setTimeout(() => { this.asideMousedown = false; }, 0);
      }, { once: true });
    },
    handler() {
      this.$emit('close', true);
    },
    middleware(event) {
      if (this.asideMousedown) return false;
      return event.target.classList && event.target.classList.contains('overlay');
    },
  },
};
</script>
